/* @flow */

import type { Tools } from './Common';
import type { Point } from './Common';
import {
  GridWidth,
  GridHeight,
  FloorHeight,
  ParticleVerticalMaxSpeed,
  ParticleVerticalAcceleration,
  ParticleHorizontalSlowRate,
} from './Common';
import LineSegment from './LineSegment';
import Goal from './Goal';
import Ramp from './Ramp';

export default class Particle {
  position: Point;
  oldPosition: Point;
  lineSegment: LineSegment;
  color = '';
  speed = {};
  timeRemaining = 0;
  dead = true;
  thickness: number;
  lineLengthMultiplier: number;
  gravity: number;

  spawn(position: Point, color: string) {
    this.lineSegment = new LineSegment();
    this.position = position;
    this.color = color;

    // variance is between [-1, 1)
    const widthVariance = 1 - 2 * Math.random();
    this.thickness = 7.5 + 3 * widthVariance;

    // adds some nice visual parralax, minimal spread on bounce
    const speedVariance = 1 - 2 * Math.random();
    this.gravity = ParticleVerticalMaxSpeed * (1 + speedVariance / 8);

    this.speed = {
      x: 0,
      y: 0 - this.gravity,
    };
    this.timeRemaining = 200;
    this.dead = false;
  }

  draw(tools: Tools) {
    const { thickness, lineSegment } = this;
    tools.ctx.beginPath();
    tools.ctx.moveTo(lineSegment.from.x, GridHeight - lineSegment.from.y);
    tools.ctx.lineTo(lineSegment.to.x, GridHeight - lineSegment.to.y);
    tools.ctx.lineWidth = thickness;
    tools.ctx.stroke();
  }

  step(goals: Array<Goal>, ramps: Array<Ramp>) {
    if (this.dead) {
      return;
    }

    // check age
    this.timeRemaining -= 1;

    // apply speed, update position
    this.oldPosition = { ...this.position };
    this.position.x += this.speed.x;
    this.position.y += this.speed.y;
    this.lineSegment.setFromTo(this.oldPosition, this.position);

    // check for goal
    let containingGoal = null;
    goals.forEach(g => {
      if (g.contains(this.position)) {
        containingGoal = g;
      }
    });
    if (containingGoal) {
      containingGoal.feedParticle(this);
      this.dead = true;
      return;
    }

    // check for bounce
    let intersection = null;
    ramps.forEach(ramp => {
      const intersectionPoint = ramp.lineSegment.findIntersection(this.lineSegment);
      if (intersectionPoint) {
        intersection = {
          ramp: ramp,
          point: intersectionPoint,
        };
      }
    });
    if (!intersection) {
      // check if moved into ramp
      ramps.forEach(ramp => {
        const rampContains = ramp.contains(this.lineSegment.to);
        if (rampContains) {
          intersection = {
            ramp: ramp,
            point: rampContains,
          };
        }
      });
    }

    if (intersection) {
      // set position to intersection
      this.position = intersection.point;

      // alter speed
      this.speed = intersection.ramp.bounce(this.speed);

      // partially apply new speed to escape the ramp
      this.position.x += this.speed.x / 10;
      this.position.y += this.speed.y / 10;

      // todo if not intersection, check if ramp.contains(from)
    } else {
      // decay speed
      // todo slow down x linearly to match y?
      this.speed.x = this.speed.x * ParticleHorizontalSlowRate;
      this.speed.y = Math.max(0 - this.gravity, this.speed.y - ParticleVerticalAcceleration);
    }

    // check for out of bounds
    this.dead =
      this.dead ||
      this.timeRemaining < 0 ||
      (this.position.y < FloorHeight || this.position.x < 0 || this.position.x > GridWidth);
  }
}



// WEBPACK FOOTER //
// ./src/Particle.js